// src/component-files/ProductivityChatWindows/ProductivityChat.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Box, Typography, IconButton, TextField, Button, Paper, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SpinningModal from '../../components/ai-chat/SpinningModal';
import TaskButton from '../../components/TaskButtons/TaskButton';
import { styled } from '@mui/system';

import userLogo from '../../assets/fig_CROP.png';
import agentLogo from '../../assets/fig_agent.png';

// ────────────────── styled components ──────────────────
const ChatWindow = styled(Paper)(({ theme }) => ({
    position: 'relative',
    width: '100%', height: '100%',
    display: 'flex', flexDirection: 'column',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
}));

const ChatHeader = styled(Box)(({ theme }) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: theme.spacing(1), borderBottom: `1px solid ${theme.palette.divider}`,
}));
const ChatContent = styled(Box)(({ theme }) => ({
    flex: 1, padding: theme.spacing(1), overflowY: 'auto',
    backgroundColor: '#fff', borderRadius: theme.shape.borderRadius,
    border: '1px solid #ccc', marginBottom: theme.spacing(1),
}));
const ChatInput = styled(Box)(({ theme }) => ({
    display: 'flex', flexDirection: 'column',
    padding: theme.spacing(2), borderTop: `1px solid ${theme.palette.divider}`,
}));

// ────────────────── main component ──────────────────
export default function ProductivityChat({ onClose }) {
    const [conversation, setConversation] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const [displayedAssistant, setDisplayedAssistant] = useState('');
    const [isDataModalOpen, setIsDataModalOpen] = useState(false);
    const [dataInput, setDataInput] = useState('');
    const [chatContext, setChatContext] = useState('');

    const chatContentRef = useRef(null);
    const inputRef = useRef(null);

    // auto‑scroll
    useEffect(() => {
        chatContentRef.current?.scrollTo(0, chatContentRef.current.scrollHeight);
    }, [conversation, displayedAssistant, isThinking]);

    // word‑by‑word animation
    const animateTyping = (fullText) => {
        const words = fullText.split(' ');
        let idx = 0;
        setDisplayedAssistant('');
        const timer = setInterval(() => {
            setDisplayedAssistant(prev => (prev ? prev + ' ' : '') + words[idx]);
            idx += 1;
            if (idx >= words.length) {
                clearInterval(timer);
                setConversation(prev => [...prev, { role: 'assistant', content: fullText }]);
                setDisplayedAssistant('');
            }
        }, 10);
    };

    // send handler
    const handleSend = async () => {
        if (isThinking) return;
        const text = inputValue.trim();
        if (!text) return;

        setInputValue('');
        const updatedConv = [...conversation, { role: 'user', content: text }];
        setConversation(updatedConv);
        setIsThinking(true);

        try {
            const url = 'https://dusty-m9jaxqxn-eastus2.cognitiveservices.azure.com/openai/deployments/usda-export-4.1/chat/completions?api-version=2025-01-01-preview';
            const payload = {
                messages: [
                    { role: 'system', content: chatContext || 'You are the USDA Food Buying Guide assistant.' },
                    ...updatedConv
                ],
                max_completion_tokens: 4000,
                temperature: 1, top_p: 1, frequency_penalty: 0, presence_penalty: 0,
                model: 'gpt-4.1',
            };
            const res = await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'api-key': '9zPogvlRqdkv2swNsbaQp2Owmy7MQtKI0Nxwrz8j0go16RiKHLRjJQQJ99BDACHYHv6XJ3w3AAAAACOGekMh',
                },
            });
            const assistantMsg = res.data?.choices?.[0]?.message?.content || '[No response]';
            animateTyping(assistantMsg);
        } catch (err) {
            console.error('Azure OpenAI error:', err);
            setConversation(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
        } finally {
            setIsThinking(false);
        }
    };

    // modal data (context) add
    const handleAddData = () => {
        if (!dataInput.trim()) return;
        setChatContext(dataInput.trim());
        setDataInput('');
        setIsDataModalOpen(false);
    };

    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <ChatWindow elevation={5}>
                {/* spinner overlay */}
                {isThinking && (
                    <Box sx={{
                        position: 'absolute', inset: 0,
                        bgcolor: 'rgba(255,255,255,0.8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
                    }}>
                        <SpinningModal color="primary" size={80} />
                    </Box>
                )}

                {/* header */}
                <ChatHeader>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h6" sx={{ fontSize: '22pt' }}>
                            Productivity Chat
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => setIsDataModalOpen(true)}
                            sx={{ bgcolor: '#333', color: '#fff', textTransform: 'none', '&:hover': { bgcolor: '#555' } }}
                        >
                            Add Chat Context Data
                        </Button>
                    </Box>
                    {onClose && (
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </ChatHeader>

                {/* content */}
                <ChatContent ref={chatContentRef}>
                    {conversation.map((msg, i) => (
                        <Box key={i} display="flex" alignItems="flex-start" mb={1}>
                            <Avatar src={msg.role === 'assistant' ? agentLogo : userLogo} sx={{ mr: 1, width: 40, height: 40 }} />
                            <TaskButton content={msg.content} />
                        </Box>
                    ))}

                    {displayedAssistant && (
                        <Box display="flex" alignItems="flex-start" mb={1}>
                            <Avatar src={agentLogo} sx={{ mr: 1, width: 40, height: 40 }} />
                            <TaskButton content={displayedAssistant} />
                        </Box>
                    )}
                </ChatContent>

                {/* input */}
                <ChatInput>
                    <TextField
                        inputRef={inputRef}
                        fullWidth multiline minRows={3} maxRows={6}
                        placeholder="Type your message…"
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); inputRef.current?.blur(); } }}
                        variant="outlined"
                        sx={{
                            p: 1, fontSize: 16, lineHeight: 1.5,
                            bgcolor: '#001f3f', borderRadius: 1,
                            '& .MuiInputBase-input': { color: '#fff', fontWeight: 'bold', whiteSpace: 'pre-wrap' }
                        }}
                    />
                    <Button variant="contained" color="primary" sx={{ mt: 1, alignSelf: 'flex-end' }} onClick={handleSend}>
                        Send
                    </Button>
                </ChatInput>
            </ChatWindow>

            {/* context dialog */}
            <Dialog open={isDataModalOpen} onClose={() => setIsDataModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add Chat Context Data</DialogTitle>
                <DialogContent>
                    <TextField
                        value={dataInput}
                        onChange={e => setDataInput(e.target.value)}
                        placeholder="Enter chat context data…"
                        multiline minRows={4} fullWidth variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDataModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddData} variant="contained" color="primary">Add Data</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
